import { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import html2pdf from 'html2pdf.js';
import { Quotation } from '../../services/quotationService';
import { settingService } from '../../services/settingService';
import { getMediaUrl } from '../../utils/media';

interface QuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation;
  onConvert?: () => void;
  /** 'proforma' renders a downloadable Pro-Forma Invoice preview instead of the Quotation preview. Not persisted anywhere. */
  documentType?: 'quotation' | 'proforma';
}

interface CompanySettings {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  tax_id?: string;
}

export const QuotationModal = ({
  isOpen,
  onClose,
  quotation,
  onConvert,
  documentType = 'quotation',
}: QuotationModalProps) => {
  const isProforma = documentType === 'proforma';

  const [companySettings, setCompanySettings] =
    useState<CompanySettings>({
      name: 'Aperture Camera Store',
      address: 'Nairobi, Kenya',
      phone: '+254 700 000 000',
      email: 'info@aperture.com',
    });

  const [proformaTerms, setProformaTerms] =
    useState<string | null>(null);

  const [isDownloading, setIsDownloading] =
    useState(false);

  /**
   * Reference to the actual A4 document.
   * This is what gets converted to PDF.
   */
  const documentRef =
    useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCompanySettings();

      if (isProforma) {
        fetchProformaTerms();
      }
    }
  }, [isOpen, isProforma]);

  const fetchCompanySettings = async () => {
    try {
      const response =
        await settingService.getCompanySettings();

      if (response.data?.data) {
        setCompanySettings({
          name:
            response.data.data.name ||
            companySettings.name,

          address:
            response.data.data.address ||
            companySettings.address,

          phone:
            response.data.data.phone ||
            companySettings.phone,

          email:
            response.data.data.email ||
            companySettings.email,

          /*
           * Keep the original logo value exactly as
           * returned by your settings service.
           */
          logo: response.data.data.logo,

          tax_id:
            response.data.data.tax_id,
        });
      }
    } catch {
      // Use defaults
    }
  };

  const fetchProformaTerms = async () => {
    try {
      const response =
        await settingService.getAllSettings();

      const rows: any[] =
        response.data?.data || [];

      const invoiceTerms = rows.find(
        (r) =>
          r.setting_key ===
          'default_invoice_terms'
      )?.setting_value;

      setProformaTerms(
        invoiceTerms || null
      );
    } catch {
      setProformaTerms(null);
    }
  };

  // Preview-only relabeling: QT-xxxx -> PF-xxxx.
  // Never persisted, display only.
  const documentNumber = isProforma
    ? quotation.quotation_number.replace(
        /^QT/,
        'PF'
      )
    : quotation.quotation_number;

  const termsToShow = isProforma
    ? proformaTerms
    : quotation.terms_conditions;

  const handlePrint = () => {
    window.print();
  };

  /**
   * Wait for all images inside the document to finish
   * loading before html2pdf captures the document.
   *
   * This is important because product images are
   * loaded from your media URLs.
   */
  const waitForImages = async (
    element: HTMLElement
  ) => {
    const images =
      Array.from(
        element.querySelectorAll('img')
      );

    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            /*
             * Image already loaded successfully.
             */
            if (
              img.complete &&
              img.naturalWidth > 0
            ) {
              resolve();
              return;
            }

            /*
             * Image failed to load.
             * We still resolve so the PDF can continue.
             */
            const handleComplete = () => {
              resolve();
            };

            img.addEventListener(
              'load',
              handleComplete,
              {
                once: true,
              }
            );

            img.addEventListener(
              'error',
              handleComplete,
              {
                once: true,
              }
            );

            /*
             * Prevent a broken image from keeping
             * the PDF generation waiting forever.
             */
            setTimeout(
              resolve,
              15000
            );
          })
      )
    );
  };

  /**
   * Download the visible quotation/proforma document
   * as an A4 PDF.
   */
  const handleDownloadPdf = async () => {
    if (!documentRef.current) {
      console.error(
        'Quotation document not found.'
      );
      return;
    }

    try {
      setIsDownloading(true);

      const element =
        documentRef.current;

      /*
       * Allow React/browser rendering to complete.
       */
      await new Promise((resolve) =>
        setTimeout(resolve, 300)
      );

      /*
       * Wait for company logo and product images.
       */
      await waitForImages(element);

      /*
       * Make sure the images have actually been
       * decoded by the browser before capturing.
       */
      const images =
        Array.from(
          element.querySelectorAll('img')
        );

      for (const img of images) {
        try {
          if (
            img.complete &&
            img.naturalWidth > 0 &&
            'decode' in img
          ) {
            await img.decode();
          }
        } catch {
          /*
           * Ignore decode errors.
           * html2canvas can still attempt to capture
           * the image.
           */
        }
      }

      const filename = isProforma
        ? `${documentNumber}-Proforma-Invoice.pdf`
        : `${documentNumber}-Quotation.pdf`;

      const options = {
        margin: 0,

        filename,

        image: {
          type: 'jpeg' as const,
          quality: 0.98,
        },

        html2canvas: {
          /*
           * Higher scale gives better PDF quality.
           */
          scale: 2,

          /*
           * Allow html2canvas to use images from
           * the existing document.
           */
          useCORS: true,

          /*
           * Important for existing image URLs.
           */
          allowTaint: true,

          backgroundColor: '#ffffff',

          logging: false,

          imageTimeout: 15000,

          /*
           * Prevent the modal viewport from clipping
           * the document during capture.
           */
          scrollX: 0,
          scrollY: 0,

          windowWidth:
            document.documentElement
              .scrollWidth,

          windowHeight:
            document.documentElement
              .scrollHeight,
        },

        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation:
            'portrait' as const,
        },

        pagebreak: {
          mode: [
            'css',
            'legacy',
          ],
        },
      };

      await html2pdf()
        .set(options)
        .from(element)
        .save();
    } catch (error) {
      console.error(
        'Failed to generate PDF:',
        error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const getCustomerName = () => {
    if (quotation.customers) {
      return (
        quotation.customers.company_name ||
        `${quotation.customers.first_name || ''} ${
          quotation.customers.last_name || ''
        }`.trim()
      );
    }

    return (
      quotation.customer_name ||
      'Walk-in Customer'
    );
  };

  const getCustomerAddress = () => {
    // Address is not always available in the customer object
    return '';
  };

  const getCustomerEmail = () => {
    if (quotation.customers?.email) {
      return quotation.customers.email;
    }

    return quotation.customer_email || '';
  };

  const getCustomerPhone = () => {
    if (quotation.customers?.phone) {
      return quotation.customers.phone;
    }

    return quotation.customer_phone || '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto">

        <div className="flex min-h-full items-center justify-center p-4">

          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden">

              <h2 className="text-lg font-semibold text-gray-900">
                {isProforma
                  ? 'Pro-Forma Invoice Preview'
                  : 'Quotation Preview'}
              </h2>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

            </div>

            {/* Document */}
            <div className="p-8 overflow-auto max-h-[70vh] print:max-h-none print:overflow-visible">

              {/* A4 DOCUMENT */}
              <div
                ref={documentRef}
                className="mx-auto bg-white print:shadow-none"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  padding: '20mm',
                  boxShadow:
                    '0 0 10px rgba(0,0,0,0.1)',
                }}
              >

                {/* COMPANY HEADER */}
                <div className="flex justify-between items-start mb-8">

                  <div>

                    {/* COMPANY LOGO */}
                    {companySettings.logo && (
                      <img
                        src={
                          companySettings.logo
                        }
                        alt="Company Logo"
                        className="h-16 object-contain mb-2"
                        loading="eager"
                        onError={(e) => {
                          console.error(
                            'Company logo failed to load:',
                            e.currentTarget.src
                          );
                        }}
                      />
                    )}

                    <h1 className="text-2xl font-bold text-gray-900">
                      {companySettings.name}
                    </h1>

                    <div className="text-sm text-gray-600 mt-1 space-y-1">

                      {companySettings.address && (
                        <div className="flex items-center gap-1">
                          <MapPinIcon className="w-4 h-4" />
                          {companySettings.address}
                        </div>
                      )}

                      {companySettings.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="w-4 h-4" />
                          {companySettings.phone}
                        </div>
                      )}

                      {companySettings.email && (
                        <div className="flex items-center gap-1">
                          <EnvelopeIcon className="w-4 h-4" />
                          {companySettings.email}
                        </div>
                      )}

                    </div>
                  </div>

                  <div className="text-right">

                    <h2 className="text-3xl font-bold text-gray-400 uppercase tracking-wider">
                      {isProforma
                        ? 'Pro-Forma Invoice'
                        : 'Quotation'}
                    </h2>

                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {documentNumber}
                    </p>

                  </div>

                </div>

                {/* QUOTE TO / DETAILS */}
                <div className="flex justify-between mb-8">

                  <div className="w-1/2 pr-4">

                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      {isProforma
                        ? 'Invoice To:'
                        : 'Quote To:'}
                    </h3>

                    <div className="border border-gray-200 rounded-lg p-4">

                      <p className="font-semibold text-gray-900">
                        {getCustomerName()}
                      </p>

                      {getCustomerAddress() && (
                        <p className="text-gray-600 mt-1">
                          {getCustomerAddress()}
                        </p>
                      )}

                      {getCustomerEmail() && (
                        <p className="text-gray-600 mt-1">
                          {getCustomerEmail()}
                        </p>
                      )}

                      {getCustomerPhone() && (
                        <p className="text-gray-600 mt-1">
                          {getCustomerPhone()}
                        </p>
                      )}

                    </div>

                  </div>

                  <div className="w-1/2 pl-4">

                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      {isProforma
                        ? 'Invoice Details:'
                        : 'Quotation Details:'}
                    </h3>

                    <div className="space-y-2">

                      <div className="flex justify-between">

                        <span className="text-gray-600">
                          Date:
                        </span>

                        <span className="font-medium">
                          {new Date(
                            quotation.quotation_date
                          ).toLocaleDateString()}
                        </span>

                      </div>

                      <div className="flex justify-between">

                        <span className="text-gray-600">
                          Expiry Date:
                        </span>

                        <span className="font-medium">
                          {quotation.expiry_date
                            ? new Date(
                                quotation.expiry_date
                              ).toLocaleDateString()
                            : 'N/A'}
                        </span>

                      </div>

                      <div className="flex justify-between">

                        <span className="text-gray-600">
                          Status:
                        </span>

                        <span className="font-medium capitalize">
                          {quotation.status}
                        </span>

                      </div>

                    </div>

                  </div>

                </div>

                {/* ITEMS TABLE */}
                <table className="w-full mb-8">

                  <thead>
                    <tr className="bg-gray-100">

                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        #
                      </th>

                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Description
                      </th>

                      <th className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Qty
                      </th>

                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Unit Price
                      </th>

                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Discount
                      </th>

                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount
                      </th>

                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">

                    {quotation.quotation_items.map(
                      (item, index) => (
                        <tr
                          key={item.item_id}
                          className="break-inside-avoid"
                        >

                          <td className="py-4 px-4 text-sm text-gray-900 align-top">
                            {index + 1}
                          </td>

                          <td className="py-4 px-4 text-sm text-gray-900 align-top">

                            <div>

                              {item.description ||
                                item.products
                                  ?.product_name ||
                                'Unknown Item'}

                              {item.products?.sku && (
                                <span className="text-gray-500 block text-xs">
                                  SKU:{' '}
                                  {
                                    item.products
                                      .sku
                                  }
                                </span>
                              )}

                            </div>

                            {/* SPECIFICATIONS */}
                            {(item as any)
                              .quotation_item_specifications
                              ?.length > 0 && (

                              <div className="mt-2 text-xs text-gray-600">

                                <span className="font-medium">
                                  Specs:
                                </span>{' '}

                                {(item as any)
                                  .quotation_item_specifications
                                  .map(
                                    (
                                      spec: any
                                    ) => (
                                      <span
                                        key={
                                          spec.spec_id
                                        }
                                        className="inline-block mr-2"
                                      >
                                        {
                                          spec.spec_name
                                        }
                                        :{' '}
                                        {
                                          spec.spec_value
                                        }
                                      </span>
                                    )
                                  )}

                              </div>
                            )}

                            {/* PRODUCT IMAGES */}
                            {(item as any)
                              .quotation_item_images
                              ?.length > 0 && (

                              <div className="mt-2 flex gap-2 flex-wrap">

                                {(item as any)
                                  .quotation_item_images
                                  .map(
                                    (
                                      img: any
                                    ) => (
                                      <img
                                        key={
                                          img.image_id
                                        }
                                        src={getMediaUrl(
                                          img.image_url
                                        )}
                                        alt={
                                          img.caption ||
                                          'Product image'
                                        }
                                        className="h-16 w-16 object-cover rounded border border-gray-200"
                                        loading="eager"
                                        onError={(
                                          
                                        ) => {
                                          console.error(
                                            'Product image failed to load:',
                                            getMediaUrl(
                                              img.image_url
                                            )
                                          );

                                          /*
                                           * Keep the image
                                           * element visible
                                           * for debugging
                                           * instead of hiding
                                           * it.
                                           */
                                        }}
                                      />
                                    )
                                  )}

                              </div>
                            )}

                          </td>

                          <td className="py-4 px-4 text-sm text-gray-900 text-center align-top">
                            {item.quantity}
                          </td>

                          <td className="py-4 px-4 text-sm text-gray-900 text-right align-top">
                            KSh{' '}
                            {Number(
                              item.unit_price
                            ).toLocaleString()}
                          </td>

                          <td className="py-4 px-4 text-sm text-gray-900 text-right align-top">

                            {item.discount_amount
                              ? `KSh ${Number(
                                  item.discount_amount
                                ).toLocaleString()}`
                              : '-'}

                          </td>

                          <td className="py-4 px-4 text-sm text-gray-900 text-right font-medium align-top">

                            KSh{' '}
                            {Number(
                              item.line_total
                            ).toLocaleString()}

                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </table>

                {/* TOTALS */}
                <div className="flex justify-end mb-8">

                  <div className="w-1/2 space-y-2">

                    <div className="flex justify-between py-2 border-b border-gray-200">

                      <span className="text-gray-600">
                        Subtotal:
                      </span>

                      <span className="font-medium">
                        KSh{' '}
                        {Number(
                          quotation.subtotal
                        ).toLocaleString()}
                      </span>

                    </div>

                    {Number(
                      quotation.discount_amount
                    ) > 0 && (

                      <div className="flex justify-between py-2 border-b border-gray-200">

                        <span className="text-gray-600">

                          Discount
                          {quotation.discount_percent
                            ? ` (${quotation.discount_percent}%)`
                            : ''}
                          :

                        </span>

                        <span className="font-medium text-red-600">

                          -KSh{' '}
                          {Number(
                            quotation.discount_amount
                          ).toLocaleString()}

                        </span>

                      </div>
                    )}

                    {Number(
                      quotation.tax_amount
                    ) > 0 && (

                      <div className="flex justify-between py-2 border-b border-gray-200">

                        <span className="text-gray-600">

                          {quotation.vat_type ===
                          'vat_exclusive'
                            ? `VAT Excl. (${
                                quotation.tax_percent ??
                                0
                              }%)`
                            : quotation.vat_type ===
                              'vat_inclusive'
                            ? `VAT Incl. (${
                                quotation.tax_percent ??
                                0
                              }%)`
                            : 'VAT'}
                          :

                        </span>

                        <span className="font-medium">

                          KSh{' '}
                          {Number(
                            quotation.tax_amount
                          ).toLocaleString()}

                        </span>

                      </div>
                    )}

                    <div className="flex justify-between py-3 border-t-2 border-gray-900">

                      <span className="text-lg font-bold text-gray-900">
                        Total:
                      </span>

                      <span className="text-lg font-bold text-gray-900">

                        KSh{' '}
                        {Number(
                          quotation.total_amount
                        ).toLocaleString()}

                      </span>

                    </div>

                  </div>

                </div>

                {/* NOTES */}
                {quotation.notes && (
                  <div className="mb-6">

                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      Notes:
                    </h3>

                    <p className="text-gray-600 whitespace-pre-wrap">
                      {quotation.notes}
                    </p>

                  </div>
                )}

                {/* TERMS */}
                {termsToShow && (
                  <div className="mb-6">

                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      Terms & Conditions:
                    </h3>

                    <p className="text-gray-600 whitespace-pre-wrap">
                      {termsToShow}
                    </p>

                  </div>
                )}

                {/* FOOTER */}
                <div className="mt-12 pt-8 border-t border-gray-200 text-center">

                  <p className="text-sm text-gray-500">

                    Thank you for your business!
                    This{' '}
                    {isProforma
                      ? 'invoice'
                      : 'quotation'}{' '}
                    is valid until{' '}

                    {quotation.expiry_date
                      ? new Date(
                          quotation.expiry_date
                        ).toLocaleDateString()
                      : 'further notice'}.

                  </p>

                </div>

              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 print:hidden">

              {/* PRINT */}
              <button
                onClick={handlePrint}
                disabled={isDownloading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >

                <PrinterIcon className="w-5 h-5" />

                Print

              </button>

              {/* DOWNLOAD PDF */}
              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >

                <DocumentArrowDownIcon className="w-5 h-5" />

                {isDownloading
                  ? 'Generating PDF...'
                  : 'Download PDF'}

              </button>

              {/* CONVERT TO SALE */}
              {!isProforma &&
                quotation.status !==
                  'converted' &&
                onConvert && (

                  <button
                    onClick={onConvert}
                    disabled={isDownloading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >

                    <DocumentArrowDownIcon className="w-5 h-5" />

                    Convert to Sale

                  </button>
                )}

            </div>

          </div>
        </div>
      </div>

      {/* PRINT STYLES */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /*
           * Hide everything except the document
           * when printing.
           */
          body * {
            visibility: hidden;
          }

          [data-quotation-document],
          [data-quotation-document] * {
            visibility: visible;
          }

          [data-quotation-document] {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm !important;
            min-height: 297mm !important;
          }
        }
      `}</style>

    </div>
  );
};
